/**
 * Documentation System Types
 *
 * Firestore-based documentation with context-aware help.
 * Collection: 'documentation'
 *
 * Context triggers follow pattern: {toolId}:{context}
 * Examples:
 *   - "episode-composer:episode-detail"
 *   - "episode-composer:episode-listing"
 *   - "episode-composer:article-picker" (for modals/dialogs)
 */

/**
 * Document category for grouping in browse mode
 */
export type DocumentationCategory =
  | 'status-help'      // Help docs for specific workflow statuses
  | 'overview'         // General overview and intro docs
  | 'how-to'           // Step-by-step guides
  | 'troubleshooting'  // Common issues and solutions
  | 'reference'        // Technical reference docs
  | 'release-note'     // Release notes and feature announcements
  | 'spotlight';       // Contextual feature spotlights (banners, tooltips)

/**
 * Display type for release notes and spotlights
 */
export type DisplayType =
  | 'banner'    // Dismissible banner at top of target area
  | 'panel';    // Shows in help panel only

/**
 * Priority level for release notes and spotlights
 */
export type Priority = 'low' | 'normal' | 'high' | 'critical';

/**
 * Tool identifiers for documentation scoping
 */
export type ToolId =
  | 'episode-composer'     // Podcast manager / episode composer
  | 'media-orchestrator'   // Parent platform dashboard
  | 'video-asset-manager'; // Video asset management tool

/**
 * A documentation document stored in Firestore
 */
export interface DocumentationDoc {
  /** Firestore document ID */
  id: string;

  /** Tool this doc belongs to (e.g., 'episode-composer') */
  toolId: ToolId;

  /** Unique doc identifier within the tool (e.g., 'status-draft') */
  docId: string;

  /** Display title */
  title: string;

  /** Optional subtitle/tagline */
  subtitle?: string;

  /** Markdown content */
  content: string;

  /** Category for browse mode grouping */
  category: DocumentationCategory;

  /** Sort order within category (lower = first) */
  sortOrder: number;

  /**
   * Context triggers that activate this doc.
   * Array of strings following pattern: {toolId}:{context}
   * A doc can have multiple triggers to appear in multiple contexts.
   *
   * Examples:
   *   - ["episode-composer:episode-detail"] - shows on episode detail page
   *   - ["episode-composer:episode-listing"] - shows on episode listing page
   *   - ["episode-composer:article-picker"] - shows in article picker modal
   */
  contextTriggers: string[];

  // ===== Fields for release-note and spotlight categories =====

  /** Version string for release notes (e.g., "1.0.0", "2.1", "2024.01") */
  version?: string;

  /**
   * How to display this doc (for release-note and spotlight only)
   * - 'banner': Dismissible banner at top of target area
   * - 'panel': Shows in help panel only
   */
  displayType?: DisplayType;

  /** Priority level (for release-note and spotlight only) */
  priority?: Priority;

  /** When to start showing this doc (null = immediately) */
  activeFrom?: Date | null;

  /** When to stop showing this doc (null = use displayDays) */
  activeUntil?: Date | null;

  /**
   * Number of days to show the doc after activeFrom (default: 5)
   * Only used if activeUntil is null
   */
  displayDays?: number;

  /** Whether this doc is currently active */
  isActive?: boolean;

  /** Metadata */
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * Input for creating a new documentation doc
 */
export interface CreateDocumentationInput {
  toolId: ToolId;
  docId: string;
  title: string;
  subtitle?: string;
  content: string;
  category: DocumentationCategory;
  sortOrder: number;
  contextTriggers: string[];
  // Optional fields for release-note and spotlight
  displayType?: DisplayType;
  priority?: Priority;
  activeFrom?: Date | null;
  activeUntil?: Date | null;
  displayDays?: number;
  isActive?: boolean;
}

/**
 * Input for updating an existing documentation doc
 */
export interface UpdateDocumentationInput {
  title?: string;
  subtitle?: string;
  content?: string;
  category?: DocumentationCategory;
  sortOrder?: number;
  contextTriggers?: string[];
  // Optional fields for release-note and spotlight
  displayType?: DisplayType;
  priority?: Priority;
  activeFrom?: Date | null;
  activeUntil?: Date | null;
  displayDays?: number;
  isActive?: boolean;
}

/**
 * Documentation grouped by category for browse mode
 */
export interface DocumentationByCategory {
  category: DocumentationCategory;
  label: string;
  docs: DocumentationDoc[];
}

/**
 * Category display labels
 */
export const CATEGORY_LABELS: Record<DocumentationCategory, string> = {
  'status-help': 'Status Help',
  'overview': 'Overview',
  'how-to': 'How-To Guides',
  'troubleshooting': 'Troubleshooting',
  'reference': 'Reference',
  'release-note': 'Release Notes',
  'spotlight': 'Feature Spotlights',
};

/**
 * Category sort order for display
 */
export const CATEGORY_ORDER: DocumentationCategory[] = [
  'overview',
  'status-help',
  'how-to',
  'troubleshooting',
  'reference',
  'release-note',
  'spotlight',
];

/**
 * Display type labels
 */
export const DISPLAY_TYPE_LABELS: Record<DisplayType, string> = {
  'banner': 'Banner',
  'panel': 'Help Panel Only',
};

/**
 * Priority labels
 */
export const PRIORITY_LABELS: Record<Priority, string> = {
  'low': 'Low',
  'normal': 'Normal',
  'high': 'High',
  'critical': 'Critical',
};

/**
 * Default display days for release notes and spotlights
 */
export const DEFAULT_DISPLAY_DAYS = 5;

/**
 * Known context triggers for targeting
 * These are the targetable areas in the application
 */
export const KNOWN_CONTEXT_TRIGGERS: Array<{ value: string; label: string; description: string }> = [
  // Episode Composer UI areas (implemented)
  { value: 'episode-composer:article-picker', label: 'Article Picker Modal', description: 'Add Articles dialog' },
  { value: 'episode-composer:episode-listing', label: 'Episode Listing', description: 'Episode listing page' },
  { value: 'episode-composer:episode-detail', label: 'Episode Detail', description: 'Episode detail page' },
  // Media Orchestrator platform areas
  { value: 'media-orchestrator:dashboard', label: 'Platform Dashboard', description: 'Main platform dashboard' },
  { value: 'media-orchestrator:settings', label: 'Settings', description: 'Platform settings' },
  { value: 'media-orchestrator:tools-coming-soon', label: 'Tools Coming Soon', description: 'Dashboard tools grid placeholder card' },
];
